'use client'
import { Dialog as DialogPrimitive, VisuallyHidden } from 'radix-ui'
import React, { useRef, useState } from 'react'
import { DialogClose, DialogTitle } from '../Dialog/Dialog'
import { cn } from '@/utils'

export type ImageZoomProps = React.ComponentProps<'div'> & {
  /**
   * Zoom source URL
   */
  zoomSrc: string

  /**
   * Extra attributes forwarded to the zoomed image. Note that they land on two
   * different elements: the desktop hover layer, which is a `<div>` painting
   * `zoomSrc` as a background, and the fullscreen `<img>`. `alt` doubles as the
   * fullscreen dialog title. Because of the `<div>`, avoid image-only
   * attributes such as `src` or `loading` — they would render as invalid
   * attributes on it.
   */
  zoomProps?: React.ImgHTMLAttributes<HTMLDivElement>

  /**
   * Magnification of the desktop hover zoom — `2` renders `zoomSrc` at twice
   * the container size. Does not affect the fullscreen view. Defaults to `2`.
   */
  scale?: number

  /**
   * Accessible name for the fullscreen close button (e.g. "Close image").
   * Required — provide a localized string from the consuming app.
   */
  closeAriaLabel: string
}

export const ImageZoom = ({
  className,
  children,
  zoomSrc,
  zoomProps,
  scale = 2,
  closeAriaLabel,
  ...props
}: ImageZoomProps) => {
  const [backgroundPosition, setBackgroundPosition] = useState<string | null>(
    null,
  )

  const [isFullscreen, setIsFullscreen] = useState(false)

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - left) / width) * 100
    const y = ((e.clientY - top) / height) * 100

    setBackgroundPosition(`${x}% ${y}%`)
  }

  return (
    <picture
      className={cn(
        'relative size-full',
        {
          'cursor-zoom-in': !isFullscreen,
          'cursor-zoom-out': isFullscreen,
        },
        className,
      )}
      onMouseLeave={() => setBackgroundPosition(null)}
      onMouseMove={handleMouseMove}
      onClick={() => {
        setIsFullscreen(!isFullscreen)
        setBackgroundPosition(null)
      }}
      {...props}
    >
      {children}

      {backgroundPosition && (
        <div
          data-testid="zoomed-image"
          {...zoomProps}
          className={cn(
            'pointer-events-none absolute left-0 top-0 z-[1] hidden size-full md:block',
            {
              hidden: isFullscreen,
            },
            zoomProps?.className,
          )}
          style={{
            backgroundImage: `url(${zoomSrc})`,
            backgroundSize: (scale + 1) * 100 + '%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition,
          }}
        />
      )}

      <ImageZoomDialog
        zoomSrc={zoomSrc}
        zoomProps={zoomProps}
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        closeAriaLabel={closeAriaLabel}
      />
    </picture>
  )
}

export function ImageZoomDialog({
  open,
  onClose,
  zoomSrc,
  zoomProps,
  closeAriaLabel,
}: {
  zoomSrc: string
  open: boolean
  onClose: () => void
  zoomProps?: React.ImgHTMLAttributes<HTMLImageElement>
  /**
   * Accessible name for the fullscreen close button (e.g. "Close image").
   * Required — provide a localized string from the consuming app.
   */
  closeAriaLabel: string
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed bg-white" />

        <DialogPrimitive.Content
          className="fixed left-0 top-0 z-[99] h-screen max-h-screen w-screen cursor-zoom-out bg-white"
          data-testid="image-zoom-dialog"
          aria-describedby={undefined}
        >
          <VisuallyHidden.Root>
            <DialogTitle>{zoomProps?.alt}</DialogTitle>
          </VisuallyHidden.Root>

          <div
            className="flex h-screen w-screen items-start justify-start overflow-auto"
            style={{ minHeight: '100vh' }}
            ref={scrollContainerRef}
          >
            <img
              ref={imgRef}
              src={zoomSrc}
              className="md:max-w-auto h-full w-auto max-w-[initial] md:h-auto md:w-full"
              {...zoomProps}
              onLoad={(...e) => {
                if (imgRef.current && scrollContainerRef.current) {
                  const container = scrollContainerRef.current

                  const scrollLeft =
                    (container.scrollWidth - container.clientWidth) / 2
                  const scrollTop =
                    (container.scrollHeight - container.clientHeight) / 2

                  container.scrollTo({
                    left: scrollLeft,
                    top: scrollTop,
                    behavior: 'auto',
                  })
                }
                zoomProps?.onLoad?.(...e)
              }}
            />
          </div>

          <DialogClose
            className="mr-3xs fixed"
            closeAriaLabel={closeAriaLabel}
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
ImageZoom.Dialog = ImageZoomDialog
